import {z} from 'zod';

// Wire view of the pinned Hermes connectors_operation contract. Native Hermes
// owns transitions, installation, credentials and completion of each operation.
const identifier = z.string().min(1).max(300);
const optionalText = z.string().nullish();
export const NativeConnectionTargetSchema = z.object({
  name: identifier, kind: z.enum(['connector', 'mcp', 'plugin', 'skill']),
  action: z.enum(['authorize', 'connect', 'enable', 'install', 'reconnect']),
  state: z.enum(['pending', 'initiated', 'connected', 'skipped', 'failed', 'expired', 'not_connected']),
  resolved: z.boolean().nullish(), detail: optionalText, instructions: optionalText,
  discovery_error: optionalText, connect_url: optionalText, display: optionalText,
  description: optionalText, hint: optionalText,
  required_env: z.array(z.object({name: identifier, required: z.boolean(), secret: z.boolean(),
    default: z.string(), prompt: optionalText}).strip()).nullish(),
  scan: z.object({status: z.enum(['passed', 'warnings', 'failed']), summary: z.string()}).strip().nullish(),
  server_errors: z.array(z.object({name: z.string(), error: z.string()}).strip()).nullish(),
}).strip();
export type NativeConnectionTarget = z.infer<typeof NativeConnectionTargetSchema>;
export const NativeConnectionOperationSchema = z.object({
  op_id: identifier, seq: z.number().int().nonnegative(), deadline_at: z.number().finite().positive(),
  timeout_seconds: z.number().finite().positive().optional(),
  tool_call_id: optionalText, settled: z.boolean().optional(), settled_at: z.number().finite().nullish(),
  settled_by: z.enum(['all_resolved', 'continue', 'deadline', 'interrupt']).nullish(),
  targets: z.array(NativeConnectionTargetSchema),
}).strip();
export type NativeConnectionOperation = z.infer<typeof NativeConnectionOperationSchema>;
export const NativePendingConnectionSchema = z.object({sessionId: identifier,
  operation: NativeConnectionOperationSchema}).strict();
export type NativePendingConnection = z.infer<typeof NativePendingConnectionSchema>;

/** UI affordances follow tools/connectors/{contract,mcp,catalog}.py. */
export function nativeConnectionTargetCanApprove(target: NativeConnectionTarget): boolean {
  return target.resolved !== true && target.kind !== 'connector'
    && (target.state === 'pending' || target.state === 'failed'
      || (target.kind === 'mcp' && target.state === 'expired'));
}
export function nativeConnectionTargetCanSkip(target: NativeConnectionTarget): boolean {
  return target.resolved !== true && (target.state === 'pending' || target.state === 'initiated'
    || target.state === 'failed' || (target.kind === 'connector' && target.state === 'expired'));
}

export const NativeConnectionAnswerSchema = z.object({
  targets: z.array(z.object({name: identifier, status: z.enum(['approved', 'skipped']),
    env: z.record(z.string(), z.string()).optional()}).strict()),
  settled_by: z.literal('continue').optional(),
}).strict();

export function validateNativeConnectionAnswer(operation: NativeConnectionOperation, value: unknown) {
  // Do not include submitted credential values in validation errors.
  const parsed = NativeConnectionAnswerSchema.safeParse(value);
  if (!parsed.success) throw new Error('The connection response has invalid fields.');
  if (operation.settled === true || operation.settled_at != null) throw new Error('The connection request has ended.');
  const answer = parsed.data;
  if (!answer.targets.length && answer.settled_by !== 'continue') throw new Error('Choose a connection action.');
  const names = new Set<string>();
  for (const item of answer.targets) {
    if (names.has(item.name)) throw new Error('The connection response repeats a service.');
    names.add(item.name);
    const target = operation.targets.find(candidate => candidate.name === item.name);
    if (!target) throw new Error('The service is not part of this connection request.');
    if (item.status === 'skipped') {
      if (!nativeConnectionTargetCanSkip(target) || item.env !== undefined) throw new Error('This service cannot be skipped with this response.');
      continue;
    }
    if (!nativeConnectionTargetCanApprove(target)) throw new Error('This service does not accept approval in its current state.');
    const fields = target.required_env ?? [];
    const env = item.env ?? {};
    if (Object.keys(env).some(name => !fields.some(field => field.name === name))) {
      throw new Error('The connection response contains a field that this service did not request.');
    }
    if (fields.some(field => field.required && !(env[field.name] ?? field.default).trim())) {
      throw new Error('Fill in the required connection fields.');
    }
  }
  return answer;
}
